/**
 * Cash Flow Forecast Report
 * 
 * Forward-looking report showing expected cash inflows (AR) and outflows (AP)
 * bucketed into weekly or monthly periods based on due dates.
 * 
 * Features:
 *   - Row grouping: expand each period/project to see individual bills/invoices with hyperlinks
 *   - Column grouping: expand Net to see AR In / AP Out breakdown per period
 *   - Optimized queries: only pulls lines from bills/invoices with outstanding balances
 * 
 * Queries:
 *   1. APBILL - outstanding bill headers (with RECORD_URL, MEGAENTITYID)
 *   2. APBILLITEM - line-level balances, filtered to parent bills with balance
 *   3. ARINVOICE - outstanding invoice headers (with RECORD_URL, MEGAENTITYID)
 *   4. ARINVOICEITEM - line-level balances, filtered to parent invoices with balance
 */

import {
  toIntacctDate, isDateCurrent, parseDate,
  buildAgingBuckets, aggregateLineBalances,
} from './reportHelpers';

// ============ PERIOD HELPERS ============

const buildPeriods = (periodType, periodCount, startDate) => {
  const periods = [];
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  for (let i = 0; i < periodCount; i++) {
    const periodStart = new Date(start);
    let periodEnd;

    if (periodType === 'weekly') {
      periodStart.setDate(start.getDate() + (i * 7));
      periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 6);
    } else {
      periodStart.setMonth(start.getMonth() + i);
      if (i > 0) periodStart.setDate(1);
      periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, 0);
    }

    periodEnd.setHours(23, 59, 59, 999);

    const label = periodType === 'weekly'
      ? `${formatShortDate(periodStart)} - ${formatShortDate(periodEnd)}`
      : formatMonthLabel(periodStart);

    periods.push({ start: periodStart, end: periodEnd, label });
  }

  return periods;
};

const formatShortDate = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
const formatMonthLabel = (d) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

const getPeriodIndex = (dueDate, periods) => {
  if (!dueDate) return 0;
  for (let i = 0; i < periods.length; i++) {
    if (dueDate <= periods[i].end) return i;
  }
  return -1;
};

// ============ REPORT DEFINITION ============

const cashFlowReport = {
  id: 'cash-flow-forecast',
  name: 'Cash Flow Forecast',
  description: 'Forward-looking cash flow showing expected AR inflows and AP outflows by weekly or monthly periods. Both summary and project views include expandable row/column drill-down into individual bills and invoices.',
  category: 'Cash Management',

  parameters: [
    {
      id: 'periodType', label: 'Period Type', type: 'select',
      default: 'monthly',
      options: [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
      ],
      description: 'Time interval for forecast columns'
    },
    {
      id: 'periodCount', label: 'Number of Periods', type: 'number',
      default: 12,
      description: 'How many periods to forecast (e.g., 12 weeks or 6 months)'
    },
    {
      id: 'groupByProject', label: 'Group by Project', type: 'checkbox',
      default: false,
      description: 'Show AR/AP breakdown by project with bill/invoice drill-down.'
    },
    {
      id: 'includeRetainage', label: 'Show Retainage Separately', type: 'checkbox',
      default: false,
      description: 'Add retainage columns in the expandable AP/AR breakdowns'
    },
    {
      id: 'projectFilter', label: 'Project ID Filter', type: 'text',
      default: '',
      description: 'Optional: filter to a specific PROJECTID (leave blank for all)'
    }
  ],

  queries: [
    {
      id: 'apBills', object: 'APBILL',
      fields: [
        'RECORDNO', 'RECORDID', 'VENDORID', 'VENDORNAME',
        'WHENCREATED', 'WHENDUE', 'TOTALENTERED', 'TOTALDUE', 'TOTALRETAINED',
        'STATE', 'CURRENCY', 'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: () => `TOTALDUE != 0 AND STATE NOT IN ('Reversed','Reversal','Voided','Deleted')`
    },
    {
      id: 'apLines', object: 'APBILLITEM',
      fields: [
        'APBILL.RECORDNO', 'APBILL.RECORDID', 'APBILL.WHENDUE',
        'APBILL.VENDORNAME', 'APBILL.RECORD_URL', 'APBILL.MEGAENTITYID',
        'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED',
        'PROJECTID', 'PROJECTNAME'
      ],
      // Filter to lines from bills with outstanding balance (avoids fully-paid bill lines)
      buildFilter: () => `APBILL.TOTALDUE != 0`
    },
    {
      id: 'arInvoices', object: 'ARINVOICE',
      fields: [
        'RECORDNO', 'RECORDID', 'CUSTOMERID', 'CUSTOMERNAME',
        'WHENCREATED', 'WHENDUE', 'TOTALENTERED', 'TOTALDUE', 'TOTALRETAINED',
        'STATE', 'CURRENCY', 'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: () => `TOTALDUE != 0 AND STATE NOT IN ('Reversed','Reversal','Voided','Deleted')`
    },
    {
      id: 'arLines', object: 'ARINVOICEITEM',
      fields: [
        'ARINVOICE.RECORDNO', 'ARINVOICE.RECORDID', 'ARINVOICE.WHENDUE',
        'ARINVOICE.CUSTOMERNAME', 'ARINVOICE.RECORD_URL', 'ARINVOICE.MEGAENTITYID',
        'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED',
        'PROJECTID', 'PROJECTNAME'
      ],
      buildFilter: () => `ARINVOICE.TOTALDUE != 0`
    }
  ],

  additionalQueries: [],
  optionalColumns: [],

  process: (datasets, params) => {
    const apBills = datasets.apBills || [];
    const apLines = datasets.apLines || [];
    const arInvoices = datasets.arInvoices || [];
    const arLines = datasets.arLines || [];

    const groupByProject = params.groupByProject;
    const showRetainage = params.includeRetainage;
    const projectFilter = (params.projectFilter || '').trim();
    const periodCount = Math.max(1, Math.min(52, parseInt(params.periodCount) || 12));

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const periods = buildPeriods(params.periodType, periodCount, today);
    const excludedStates = new Set(['Reversed', 'Reversal', 'Voided', 'Deleted']);

    // Build valid bill/invoice sets and info maps
    const validApBills = new Set();
    const apDueDates = {};
    const apBillInfo = {};
    for (const bill of apBills) {
      if (excludedStates.has(String(bill.STATE || '').trim())) continue;
      const rn = String(bill.RECORDNO || '').trim();
      validApBills.add(rn);
      apDueDates[rn] = parseDate(bill.WHENDUE);
      apBillInfo[rn] = {
        recordId: bill.RECORDID || '', vendorName: bill.VENDORNAME || '',
        url: bill.RECORD_URL || '', entity: bill.MEGAENTITYID || '',
      };
    }

    const validArInvoices = new Set();
    const arDueDates = {};
    const arInvInfo = {};
    for (const inv of arInvoices) {
      if (excludedStates.has(String(inv.STATE || '').trim())) continue;
      const rn = String(inv.RECORDNO || '').trim();
      validArInvoices.add(rn);
      arDueDates[rn] = parseDate(inv.WHENDUE);
      arInvInfo[rn] = {
        recordId: inv.RECORDID || '', customerName: inv.CUSTOMERNAME || '',
        url: inv.RECORD_URL || '', entity: inv.MEGAENTITYID || '',
      };
    }

    // ============ AGGREGATION STRUCTURES ============
    const projectData = {};
    const NO_PROJECT = '(No Project)';

    const ensureProject = (projectId, projectName) => {
      const pid = projectId || NO_PROJECT;
      if (!projectData[pid]) {
        projectData[pid] = {
          projectName: projectName || pid,
          arPeriods: new Array(periodCount).fill(0),
          apPeriods: new Array(periodCount).fill(0),
          arRetPeriods: new Array(periodCount).fill(0),
          apRetPeriods: new Array(periodCount).fill(0),
        };
      }
      return projectData[pid];
    };

    // Period-level doc tracking (for summary view)
    const periodDocs = [];
    for (let i = 0; i < periodCount; i++) {
      periodDocs.push({ apDocs: {}, arDocs: {} });
    }

    // Project-level doc tracking (for project view)
    // projectDocData[projectId].apDocs[billRN] = { info..., periodBalances: {idx: balance}, total }
    const projectDocData = {};

    const ensureProjectDocData = (projectId) => {
      if (!projectDocData[projectId]) {
        projectDocData[projectId] = { apDocs: {}, arDocs: {} };
      }
      return projectDocData[projectId];
    };

    // ============ PROCESS AP LINES ============
    for (const line of apLines) {
      const parentKey = String(line['APBILL.RECORDNO'] || '').trim();
      if (!validApBills.has(parentKey)) continue;

      const amount = parseFloat(line.AMOUNT) || 0;
      const paid = parseFloat(line.TOTALPAID) || 0;
      const retained = parseFloat(line.TRX_AMOUNTRETAINED) || 0;
      const balance = amount - paid - retained;

      if (balance === 0 && retained === 0) continue;

      const projectId = String(line.PROJECTID || '').trim();
      const projectName = String(line.PROJECTNAME || '').trim();
      if (projectFilter && projectId !== projectFilter) continue;

      const dueDate = parseDate(line['APBILL.WHENDUE']) || apDueDates[parentKey] || today;
      const periodIdx = getPeriodIndex(dueDate, periods);
      if (periodIdx < 0) continue;

      const proj = ensureProject(projectId, projectName);
      proj.apPeriods[periodIdx] += balance;
      if (showRetainage) proj.apRetPeriods[periodIdx] += retained;

      // Resolve best info (prefer line-level enriched fields)
      const info = { ...(apBillInfo[parentKey] || {}) };
      if (line['APBILL.RECORD_URL']) info.url = line['APBILL.RECORD_URL'];
      if (line['APBILL.MEGAENTITYID']) info.entity = line['APBILL.MEGAENTITYID'];
      if (line['APBILL.RECORDID']) info.recordId = line['APBILL.RECORDID'];
      if (line['APBILL.VENDORNAME']) info.vendorName = line['APBILL.VENDORNAME'];

      // Period doc tracking (summary view)
      if (!periodDocs[periodIdx].apDocs[parentKey]) {
        periodDocs[periodIdx].apDocs[parentKey] = { ...info, amount: 0, paid: 0, retained: 0, balance: 0 };
      }
      const pDoc = periodDocs[periodIdx].apDocs[parentKey];
      pDoc.amount += amount; pDoc.paid += paid; pDoc.retained += retained; pDoc.balance += balance;
      Object.assign(pDoc, info); // update with best info

      // Project doc tracking (project view)
      const pid = projectId || NO_PROJECT;
      const projDocs = ensureProjectDocData(pid);
      if (!projDocs.apDocs[parentKey]) {
        projDocs.apDocs[parentKey] = { ...info, periodBalances: {}, total: 0 };
      }
      const prjDoc = projDocs.apDocs[parentKey];
      prjDoc.periodBalances[periodIdx] = (prjDoc.periodBalances[periodIdx] || 0) + balance;
      prjDoc.total += balance;
      Object.assign(prjDoc, { recordId: info.recordId, vendorName: info.vendorName, url: info.url, entity: info.entity });
    }

    // ============ PROCESS AR LINES ============
    for (const line of arLines) {
      const parentKey = String(line['ARINVOICE.RECORDNO'] || '').trim();
      if (!validArInvoices.has(parentKey)) continue;

      const amount = parseFloat(line.AMOUNT) || 0;
      const paid = parseFloat(line.TOTALPAID) || 0;
      const retained = parseFloat(line.TRX_AMOUNTRETAINED) || 0;
      const balance = amount - paid - retained;

      if (balance === 0 && retained === 0) continue;

      const projectId = String(line.PROJECTID || '').trim();
      const projectName = String(line.PROJECTNAME || '').trim();
      if (projectFilter && projectId !== projectFilter) continue;

      const dueDate = parseDate(line['ARINVOICE.WHENDUE']) || arDueDates[parentKey] || today;
      const periodIdx = getPeriodIndex(dueDate, periods);
      if (periodIdx < 0) continue;

      const proj = ensureProject(projectId, projectName);
      proj.arPeriods[periodIdx] += balance;
      if (showRetainage) proj.arRetPeriods[periodIdx] += retained;

      const info = { ...(arInvInfo[parentKey] || {}) };
      if (line['ARINVOICE.RECORD_URL']) info.url = line['ARINVOICE.RECORD_URL'];
      if (line['ARINVOICE.MEGAENTITYID']) info.entity = line['ARINVOICE.MEGAENTITYID'];
      if (line['ARINVOICE.RECORDID']) info.recordId = line['ARINVOICE.RECORDID'];
      if (line['ARINVOICE.CUSTOMERNAME']) info.customerName = line['ARINVOICE.CUSTOMERNAME'];

      // Period doc tracking
      if (!periodDocs[periodIdx].arDocs[parentKey]) {
        periodDocs[periodIdx].arDocs[parentKey] = { ...info, amount: 0, paid: 0, retained: 0, balance: 0 };
      }
      const pDoc = periodDocs[periodIdx].arDocs[parentKey];
      pDoc.amount += amount; pDoc.paid += paid; pDoc.retained += retained; pDoc.balance += balance;
      Object.assign(pDoc, info);

      // Project doc tracking
      const pid = projectId || NO_PROJECT;
      const projDocs = ensureProjectDocData(pid);
      if (!projDocs.arDocs[parentKey]) {
        projDocs.arDocs[parentKey] = { ...info, periodBalances: {}, total: 0 };
      }
      const prjDoc = projDocs.arDocs[parentKey];
      prjDoc.periodBalances[periodIdx] = (prjDoc.periodBalances[periodIdx] || 0) + balance;
      prjDoc.total += balance;
      Object.assign(prjDoc, { recordId: info.recordId, customerName: info.customerName, url: info.url, entity: info.entity });
    }

    const r2 = (v) => Math.round(v * 100) / 100;

    if (groupByProject) {
      return buildProjectView(projectData, projectDocData, periods, periodCount, showRetainage, r2);
    } else {
      return buildSummaryView(projectData, periods, periodCount, showRetainage, r2, periodDocs);
    }
  },

  formatRules: {}
};

// ============ SUMMARY VIEW (with row + column drill-down) ============

function buildSummaryView(projectData, periods, periodCount, showRetainage, r2, periodDocs) {
  // Columns: Period | AP Due | [AP Billed | AP Paid | (AP Ret)] | AR Due | [AR Invoiced | AR Received | (AR Ret)] | Net | Cumulative
  const columns = ['Period', 'AP Due'];
  const apDetailStart = 2;
  columns.push('AP Billed', 'AP Paid');
  if (showRetainage) columns.push('AP Retainage');
  const apDetailEnd = columns.length - 1;

  columns.push('AR Due');
  const arDetailStart = columns.length;
  columns.push('AR Invoiced', 'AR Received');
  if (showRetainage) columns.push('AR Retainage');
  const arDetailEnd = columns.length - 1;

  columns.push('Net Cash Flow', 'Cumulative Net');

  const formatRules = {};
  for (const col of columns) {
    if (col !== 'Period') formatRules[col] = { type: 'currency', decimals: 2 };
  }

  // Column grouping
  const apDetailIndices = [];
  for (let i = apDetailStart; i <= apDetailEnd; i++) apDetailIndices.push(i);
  const arDetailIndices = [];
  for (let i = arDetailStart; i <= arDetailEnd; i++) arDetailIndices.push(i);
  const groupedColumns = [
    { indices: apDetailIndices },
    { indices: arDetailIndices },
  ];

  // Aggregate across all projects
  const periodAR = new Array(periodCount).fill(0);
  const periodAP = new Array(periodCount).fill(0);
  const periodARRet = new Array(periodCount).fill(0);
  const periodAPRet = new Array(periodCount).fill(0);
  const periodAPBilled = new Array(periodCount).fill(0);
  const periodAPPaid = new Array(periodCount).fill(0);
  const periodARInvoiced = new Array(periodCount).fill(0);
  const periodARReceived = new Array(periodCount).fill(0);

  for (const data of Object.values(projectData)) {
    for (let i = 0; i < periodCount; i++) {
      periodAR[i] += data.arPeriods[i];
      periodAP[i] += data.apPeriods[i];
      if (showRetainage) {
        periodARRet[i] += data.arRetPeriods[i];
        periodAPRet[i] += data.apRetPeriods[i];
      }
    }
  }

  for (let i = 0; i < periodCount; i++) {
    for (const doc of Object.values(periodDocs[i].apDocs)) {
      periodAPBilled[i] += doc.amount;
      periodAPPaid[i] += doc.paid;
    }
    for (const doc of Object.values(periodDocs[i].arDocs)) {
      periodARInvoiced[i] += doc.amount;
      periodARReceived[i] += doc.paid;
    }
  }

  const reportRows = [];
  const detailRowIndices = [];
  let cumulativeNet = 0;
  let totalAR = 0, totalAP = 0;

  const emptyRow = () => {
    const row = {};
    for (const col of columns) row[col] = '';
    return row;
  };

  for (let i = 0; i < periodCount; i++) {
    const arIn = r2(periodAR[i]);
    const apOut = r2(periodAP[i]);
    const net = r2(arIn - apOut);
    cumulativeNet = r2(cumulativeNet + net);
    totalAR += arIn;
    totalAP += apOut;

    const summaryRow = {
      'Period': periods[i].label,
      'AP Due': apOut,
      'AP Billed': r2(periodAPBilled[i]),
      'AP Paid': r2(periodAPPaid[i]),
      'AR Due': arIn,
      'AR Invoiced': r2(periodARInvoiced[i]),
      'AR Received': r2(periodARReceived[i]),
      'Net Cash Flow': net,
      'Cumulative Net': cumulativeNet,
    };
    if (showRetainage) {
      summaryRow['AP Retainage'] = r2(periodAPRet[i]);
      summaryRow['AR Retainage'] = r2(periodARRet[i]);
    }
    reportRows.push(summaryRow);

    // Detail rows: AP bills
    const apDocs = Object.values(periodDocs[i].apDocs)
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
    for (const doc of apDocs) {
      if (doc.balance === 0 && doc.retained === 0) continue;
      const detailRow = emptyRow();
      detailRow['Period'] = `  ↳ ${doc.recordId || 'Bill'}${doc.vendorName ? ' — ' + doc.vendorName : ''}`;
      detailRow['AP Due'] = r2(doc.balance);
      detailRow['AP Billed'] = r2(doc.amount);
      detailRow['AP Paid'] = r2(doc.paid);
      if (showRetainage) detailRow['AP Retainage'] = r2(doc.retained);
      detailRow['_descUrl'] = doc.url || '';
      reportRows.push(detailRow);
      detailRowIndices.push(reportRows.length - 1);
    }

    // Detail rows: AR invoices
    const arDocs = Object.values(periodDocs[i].arDocs)
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
    for (const doc of arDocs) {
      if (doc.balance === 0 && doc.retained === 0) continue;
      const detailRow = emptyRow();
      detailRow['Period'] = `  ↳ ${doc.recordId || 'Invoice'}${doc.customerName ? ' — ' + doc.customerName : ''}`;
      detailRow['AR Due'] = r2(doc.balance);
      detailRow['AR Invoiced'] = r2(doc.amount);
      detailRow['AR Received'] = r2(doc.paid);
      if (showRetainage) detailRow['AR Retainage'] = r2(doc.retained);
      detailRow['_descUrl'] = doc.url || '';
      reportRows.push(detailRow);
      detailRowIndices.push(reportRows.length - 1);
    }
  }

  // Grand total
  const totalRow = {
    'Period': 'TOTAL',
    'AP Due': r2(totalAP),
    'AP Billed': r2(periodAPBilled.reduce((s, v) => s + v, 0)),
    'AP Paid': r2(periodAPPaid.reduce((s, v) => s + v, 0)),
    'AR Due': r2(totalAR),
    'AR Invoiced': r2(periodARInvoiced.reduce((s, v) => s + v, 0)),
    'AR Received': r2(periodARReceived.reduce((s, v) => s + v, 0)),
    'Net Cash Flow': r2(totalAR - totalAP),
    'Cumulative Net': cumulativeNet,
  };
  if (showRetainage) {
    totalRow['AP Retainage'] = r2(periodAPRet.reduce((s, v) => s + v, 0));
    totalRow['AR Retainage'] = r2(periodARRet.reduce((s, v) => s + v, 0));
  }
  reportRows.push(totalRow);

  formatRules['Period'] = { type: 'hyperlink', urlKey: '_descUrl' };

  return {
    columns, data: reportRows, formatRules,
    detailRowIndices, groupedColumns,
    summary: {
      lines: [
        `Forecast: ${periodCount} periods (${detailRowIndices.length} bill/invoice details)`,
        `Total AR Expected: $${r2(totalAR).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `Total AP Expected: $${r2(totalAP).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `Net Cash Flow: $${r2(totalAR - totalAP).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      ],
      totals: { grandAR: totalAR, grandAP: totalAP }
    }
  };
}

// ============ PROJECT VIEW (with row + column drill-down) ============

function buildProjectView(projectData, projectDocData, periods, periodCount, showRetainage, r2) {
  // Column layout per period: Net (summary) | AR In (detail) | AP Out (detail) [| AR Ret | AP Ret]
  // summaryRight: false means summary is LEFT of detail → expanding shows AR/AP to the right
  const columns = ['Project ID', 'Project Name'];
  const formatRules = {};
  const groupedColumns = [];

  for (let i = 0; i < periodCount; i++) {
    const label = periods[i].label;
    const netCol = `${label} Net`;
    const arCol = `${label} AR In`;
    const apCol = `${label} AP Out`;

    columns.push(netCol);
    const detailStart = columns.length;
    columns.push(arCol, apCol);
    formatRules[netCol] = { type: 'currency', decimals: 2 };
    formatRules[arCol] = { type: 'currency', decimals: 2 };
    formatRules[apCol] = { type: 'currency', decimals: 2 };

    const detailIndices = [detailStart, detailStart + 1]; // AR In, AP Out

    if (showRetainage) {
      const arRetCol = `${label} AR Ret`;
      const apRetCol = `${label} AP Ret`;
      columns.push(arRetCol, apRetCol);
      formatRules[arRetCol] = { type: 'currency', decimals: 2 };
      formatRules[apRetCol] = { type: 'currency', decimals: 2 };
      detailIndices.push(detailStart + 2, detailStart + 3);
    }

    groupedColumns.push({ indices: detailIndices });
  }

  // Total columns: Net (summary) | AR In | AP Out (detail)
  columns.push('Total Net');
  const totalDetailStart = columns.length;
  columns.push('Total AR In', 'Total AP Out');
  formatRules['Total Net'] = { type: 'currency', decimals: 2 };
  formatRules['Total AR In'] = { type: 'currency', decimals: 2 };
  formatRules['Total AP Out'] = { type: 'currency', decimals: 2 };
  groupedColumns.push({ indices: [totalDetailStart, totalDetailStart + 1] });

  // Hyperlinks for detail rows
  formatRules['Project ID'] = { type: 'hyperlink', urlKey: '_descUrl' };

  const reportRows = [];
  const detailRowIndices = [];
  const grandTotals = {
    ar: new Array(periodCount).fill(0),
    ap: new Array(periodCount).fill(0),
  };

  // Helper: empty row
  const emptyRow = () => {
    const row = {};
    for (const col of columns) row[col] = '';
    return row;
  };

  // Sort projects
  const sortedProjects = Object.entries(projectData).sort(([a], [b]) => {
    if (a === '(No Project)') return 1;
    if (b === '(No Project)') return -1;
    return a.localeCompare(b);
  });

  for (const [projectId, data] of sortedProjects) {
    const row = { 'Project ID': projectId, 'Project Name': data.projectName };
    let totalAR = 0, totalAP = 0;

    for (let i = 0; i < periodCount; i++) {
      const label = periods[i].label;
      const arIn = r2(data.arPeriods[i]);
      const apOut = r2(data.apPeriods[i]);
      const net = r2(arIn - apOut);

      row[`${label} Net`] = net;
      row[`${label} AR In`] = arIn;
      row[`${label} AP Out`] = apOut;
      totalAR += arIn;
      totalAP += apOut;
      grandTotals.ar[i] += arIn;
      grandTotals.ap[i] += apOut;

      if (showRetainage) {
        row[`${label} AR Ret`] = r2(data.arRetPeriods[i]);
        row[`${label} AP Ret`] = r2(data.apRetPeriods[i]);
      }
    }

    row['Total Net'] = r2(totalAR - totalAP);
    row['Total AR In'] = r2(totalAR);
    row['Total AP Out'] = r2(totalAP);
    row['_descUrl'] = '';
    reportRows.push(row);

    // Detail rows: bills and invoices for this project
    const projDocs = projectDocData[projectId] || { apDocs: {}, arDocs: {} };

    // AP bills
    const apDocsSorted = Object.values(projDocs.apDocs)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

    for (const doc of apDocsSorted) {
      if (Math.abs(doc.total) < 0.005) continue;
      const detailRow = emptyRow();
      detailRow['Project ID'] = `  ↳ ${doc.recordId || 'Bill'}${doc.vendorName ? ' — ' + doc.vendorName : ''}`;
      detailRow['_descUrl'] = doc.url || '';

      let docTotalAP = 0;
      for (let i = 0; i < periodCount; i++) {
        const label = periods[i].label;
        const periodBal = r2(doc.periodBalances[i] || 0);
        if (periodBal !== 0) {
          detailRow[`${label} Net`] = periodBal * -1; // AP is negative in net
          detailRow[`${label} AP Out`] = periodBal;
          docTotalAP += periodBal;
        }
      }
      detailRow['Total Net'] = r2(docTotalAP * -1);
      detailRow['Total AP Out'] = r2(docTotalAP);

      reportRows.push(detailRow);
      detailRowIndices.push(reportRows.length - 1);
    }

    // AR invoices
    const arDocsSorted = Object.values(projDocs.arDocs)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

    for (const doc of arDocsSorted) {
      if (Math.abs(doc.total) < 0.005) continue;
      const detailRow = emptyRow();
      detailRow['Project ID'] = `  ↳ ${doc.recordId || 'Invoice'}${doc.customerName ? ' — ' + doc.customerName : ''}`;
      detailRow['_descUrl'] = doc.url || '';

      let docTotalAR = 0;
      for (let i = 0; i < periodCount; i++) {
        const label = periods[i].label;
        const periodBal = r2(doc.periodBalances[i] || 0);
        if (periodBal !== 0) {
          detailRow[`${label} Net`] = periodBal; // AR is positive in net
          detailRow[`${label} AR In`] = periodBal;
          docTotalAR += periodBal;
        }
      }
      detailRow['Total Net'] = r2(docTotalAR);
      detailRow['Total AR In'] = r2(docTotalAR);

      reportRows.push(detailRow);
      detailRowIndices.push(reportRows.length - 1);
    }
  }

  // Grand total row
  const grandRow = { 'Project ID': 'TOTAL', 'Project Name': '', '_descUrl': '' };
  let gAR = 0, gAP = 0;
  for (let i = 0; i < periodCount; i++) {
    const label = periods[i].label;
    const arIn = r2(grandTotals.ar[i]);
    const apOut = r2(grandTotals.ap[i]);
    grandRow[`${label} Net`] = r2(arIn - apOut);
    grandRow[`${label} AR In`] = arIn;
    grandRow[`${label} AP Out`] = apOut;
    gAR += arIn;
    gAP += apOut;
  }
  grandRow['Total Net'] = r2(gAR - gAP);
  grandRow['Total AR In'] = r2(gAR);
  grandRow['Total AP Out'] = r2(gAP);
  reportRows.push(grandRow);

  return {
    columns, data: reportRows, formatRules,
    detailRowIndices, groupedColumns,
    summary: {
      lines: [
        `Forecast: ${periodCount} periods (${sortedProjects.length} projects, ${detailRowIndices.length} details)`,
        `Total AR Expected: $${r2(gAR).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `Total AP Expected: $${r2(gAP).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `Net Cash Flow: $${r2(gAR - gAP).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      ],
      totals: { grandAR: gAR, grandAP: gAP }
    }
  };
}

export default cashFlowReport;